import {
  login,
  logout,
  currentAuth,
  isAdmin,
  listUsers,
  createUser,
  deleteUser,
  setPassword,
  setRole,
  cloudStatus,
  bootstrapAdmin,
  migrateToCloud,
  MAX_USERS,
  type Role,
  type UserRow,
} from "./auth";
import { loadLegacyUsers, legacyUserData } from "./legacy";
import { toggleMode, themeIcon, currentMode } from "../core/appTheme";
import { mountPracticeAdmin } from "../admin/practiceAdmin";
import { confirmDialog } from "../practice/dialog";

const LOGO = `<svg class="brand-logo" viewBox="0 0 32 32" width="34" height="34"><rect width="32" height="32" rx="8"/><path d="M7 21l5-7 4 3 9-10" stroke-width="2.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
}

function authShell(inner: string): string {
  return `
    <div class="auth-wrap">
      <button class="mini-btn icon-btn theme-float" id="login-theme" title="Toggle light / dark">${themeIcon(currentMode())}</button>
      <div class="auth-card">
        <div class="auth-brand">${LOGO}<div><div class="auth-title">myTV</div><div class="auth-sub">Charting · Replay · Journal</div></div></div>
        ${inner}
      </div>
    </div>`;
}

// ---------------- Login (+ first-run setup) ----------------
export async function showLogin(onSuccess: () => void) {
  const app = document.getElementById("app")!;
  const st = await cloudStatus();

  if (!st.configured) {
    app.innerHTML = authShell(`
      <div class="auth-setup">
        <b>Cloud isn't connected yet</b>
        <p>Copy <code>.env.example</code> to <code>.env.local</code>, fill in your Supabase URL and keys, then restart <code>npm run dev</code>.</p>
      </div>`);
  } else if (st.dbError) {
    app.innerHTML = authShell(`
      <div class="auth-setup">
        <b>Can't reach the database</b>
        <p>The Supabase project isn't answering (${st.dbError.replace(/[<>&]/g, "")}). If it was paused, restore it in the Supabase dashboard, then reload this page.</p>
      </div>`);
  } else if (!st.hasAdmin && st.serverConfigured) {
    app.innerHTML = authShell(`
      <form id="boot-form">
        <div class="auth-setup"><b>Welcome — create the admin account</b><p>This runs once. Afterwards, sign in normally and import your existing data from the Admin page.</p></div>
        <div class="auth-field"><label>Admin username</label><input id="bt-user" value="rhythm" autocomplete="username" /></div>
        <div class="auth-field"><label>Password</label><input id="bt-pass" type="password" autocomplete="new-password" placeholder="at least 6 characters" /></div>
        <div class="auth-error" id="bt-err"></div>
        <button class="auth-btn" type="submit">Create admin & sign in</button>
      </form>`);
    const err = app.querySelector<HTMLElement>("#bt-err")!;
    app.querySelector("#boot-form")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      err.textContent = "";
      const u = app.querySelector<HTMLInputElement>("#bt-user")!.value.trim();
      const p = app.querySelector<HTMLInputElement>("#bt-pass")!.value;
      const r = await bootstrapAdmin(u, p);
      if (!r.ok) return void (err.textContent = r.error ?? "Setup failed");
      const l = await login(u, p);
      if (l.ok) onSuccess();
      else err.textContent = l.error ?? "Login failed";
    });
  } else {
    app.innerHTML = authShell(`
      <form id="login-form">
        <div class="auth-field"><label>Username</label><input id="lg-user" autocomplete="username" autofocus /></div>
        <div class="auth-field"><label>Password</label><input id="lg-pass" type="password" autocomplete="current-password" /></div>
        <div class="auth-error" id="lg-err"></div>
        <button class="auth-btn" type="submit">Sign in</button>
      </form>`);
    const err = app.querySelector<HTMLElement>("#lg-err")!;
    const form = app.querySelector<HTMLFormElement>("#login-form")!;
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      err.textContent = "";
      const btn = form.querySelector<HTMLButtonElement>("button")!;
      btn.disabled = true;
      btn.textContent = "Signing in…";
      const r = await login(app.querySelector<HTMLInputElement>("#lg-user")!.value, app.querySelector<HTMLInputElement>("#lg-pass")!.value);
      if (r.ok) return onSuccess();
      err.textContent = r.error || "Login failed";
      btn.disabled = false;
      btn.textContent = "Sign in";
    });
  }

  const themeBtn = app.querySelector<HTMLElement>("#login-theme")!;
  themeBtn.addEventListener("click", () => {
    toggleMode();
    themeBtn.innerHTML = themeIcon(currentMode());
  });
}

// ---------------- Home ----------------
export function showHome() {
  const app = document.getElementById("app")!;
  const auth = currentAuth()!;
  const admin = isAdmin();
  const tile = (href: string, icon: string, title: string, sub: string) =>
    `<a class="home-tile" href="${href}"><div class="home-icon">${icon}</div><div><div class="home-tt">${title}</div><div class="home-ts">${sub}</div></div></a>`;
  app.innerHTML = `
    <div class="home-wrap">
      <div class="home-top">
        <div class="auth-brand">${LOGO}<div><div class="auth-title">myTV</div><div class="auth-sub hide-sm">MNQ workstation</div></div></div>
        <div class="home-user">
          <span><span class="hide-sm">Signed in as </span><b>${esc(auth.username)}</b> <span class="badge ${admin ? "active" : "paused"}">${auth.role}</span></span>
          <button class="mini-btn icon-btn" id="home-theme" title="Toggle light / dark">${themeIcon(currentMode())}</button>
          <button class="mini-btn" id="home-logout">Log out</button>
        </div>
      </div>
      <div class="home-grid">
        ${tile("/charts", chartIcon(), "Charts", "Full TradingView-style charting & drawing")}
        ${tile("/replay_trading", replayIcon(), "Replay Trading", "Bar-replay simulator, paper trades & zones")}
        ${tile("/practice", practiceIcon(), "Practice", "Review random zones, approve or reject, see what happened")}
        ${tile("/tests", testsIcon(), "Tests", admin ? "Create timed zone tests & see who passed" : "Take timed zone-marking tests")}
        ${tile("/analysis", analysisIcon(), "Analysis", "Filter research — store data & spot edges")}
        ${tile("/tools", toolsIcon(), "Tools", "Averaging & more trading utilities")}
        ${admin ? tile("/admin", adminIcon(), "Admin", "Manage users & access") : ""}
      </div>
    </div>`;
  app.querySelector("#home-logout")!.addEventListener("click", async () => {
    await logout();
    location.href = "/";
  });
  const themeBtn = app.querySelector<HTMLElement>("#home-theme")!;
  themeBtn.addEventListener("click", () => {
    toggleMode();
    themeBtn.innerHTML = themeIcon(currentMode());
  });
}

// ---------------- Admin ----------------
export async function showAdmin() {
  if (!isAdmin()) {
    location.href = "/";
    return;
  }
  const app = document.getElementById("app")!;
  const me = currentAuth()!;

  const render = async () => {
    let users: UserRow[] = [];
    let loadErr = "";
    try {
      users = await listUsers();
    } catch (e) {
      loadErr = (e as Error).message;
    }
    const rows = users
      .map(
        (u) => `<tr>
        <td>${esc(u.username)}${u.id === me.id ? ' <span class="badge active">you</span>' : ""}</td>
        <td>
          <select data-role="${u.id}" ${u.id === me.id ? "disabled" : ""}>
            <option value="admin" ${u.role === "admin" ? "selected" : ""}>admin</option>
            <option value="user" ${u.role === "user" ? "selected" : ""}>user</option>
          </select>
        </td>
        <td>${new Date(u.created_at).toLocaleDateString()}</td>
        <td style="text-align:right">
          <button class="mini-btn" data-pass="${u.id}" data-name="${esc(u.username)}">Reset password</button>
          <button class="mini-btn danger" data-deluser="${u.id}" data-name="${esc(u.username)}" ${u.id === me.id ? "disabled" : ""}>Delete</button>
        </td>
      </tr>`
      )
      .join("");

    const legacy = loadLegacyUsers();
    const found = legacy.length
      ? `Found ${legacy.length} account${legacy.length === 1 ? "" : "s"} from before the cloud in this browser: <b>${legacy.map((u) => esc(u.username)).join(", ")}</b>.
         Their accounts, drawings, replay sessions, analysis and preferences are uploaded, plus every practice marking from the old local database.`
      : `No pre-cloud accounts were found in <b>this</b> browser at <code>${esc(location.origin)}</code> — browsers keep data per address, so if you
         used the app at a different address (e.g. localhost vs 127.0.0.1) or in another browser, open Admin there. Importing here still uploads
         every practice marking from the old local database.`;
    const migrateCard = `<div class="card" style="margin-bottom:20px" id="mig-card">
          <h3>Move local data to the cloud</h3>
          <p class="pa-sub" style="margin:0 0 12px">${found} Safe to run more than once.</p>
          <div class="form-row two">
            <div class="form-row" style="margin:0"><label>Temporary password for imported accounts</label><input id="mig-pass" type="text" placeholder="at least 6 characters" ${legacy.length ? "" : "disabled"} /></div>
            <div class="form-row" style="margin:0;justify-content:flex-end"><button class="mini-btn primary" id="mig-run" style="padding:10px">Import to cloud</button></div>
          </div>
          <div class="pa-sub" id="mig-out"></div>
        </div>`;

    app.innerHTML = `
      <div class="dash" style="max-width:1240px">
        <div class="dash-head">
          <div class="dash-title">User Management <span class="sub">${users.length}/${MAX_USERS} users · data stored in the cloud</span></div>
          <a class="mini-btn" href="/">← Home</a>
        </div>
        ${loadErr ? `<div class="card pa-error" style="margin-bottom:20px">${esc(loadErr)}</div>` : ""}
        ${migrateCard}
        <div class="card" style="margin-bottom:20px">
          <h3>Users</h3>
          <table class="tbl" style="width:100%">
            <thead><tr><th>Username</th><th>Role</th><th>Created</th><th style="text-align:right">Actions</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
        <div class="card">
          <h3>Add user</h3>
          <div class="form-row two">
            <div class="form-row" style="margin:0"><label>Username</label><input id="nu-user" placeholder="3–20 chars" /></div>
            <div class="form-row" style="margin:0"><label>Password</label><input id="nu-pass" type="password" placeholder="at least 6 characters" /></div>
          </div>
          <div class="form-row"><label>Role</label><select id="nu-role"><option value="user">user</option><option value="admin">admin</option></select></div>
          <div class="auth-error" id="nu-err"></div>
          <button class="mini-btn primary" id="nu-add" ${users.length >= MAX_USERS ? "disabled" : ""}>Add user</button>
        </div>
      </div>`;

    app.querySelectorAll<HTMLSelectElement>("[data-role]").forEach((sel) =>
      sel.addEventListener("change", async () => {
        const r = await setRole(sel.dataset.role!, sel.value as Role);
        if (!r.ok) alert(r.error);
        render();
      })
    );
    app.querySelectorAll<HTMLElement>("[data-pass]").forEach((b) =>
      b.addEventListener("click", async () => {
        const np = prompt(`New password for "${b.dataset.name}" (at least 6 characters)`);
        if (np) {
          const r = await setPassword(b.dataset.pass!, np);
          alert(r.ok ? "Password updated" : r.error);
        }
      })
    );
    app.querySelectorAll<HTMLElement>("[data-deluser]").forEach((b) =>
      b.addEventListener("click", async () => {
        const ok = await confirmDialog({
          emoji: "🗑️",
          title: `Delete user "${b.dataset.name}"?`,
          body: "This removes their account and all their cloud data (drawings, sessions, analysis, practice markings). It can't be undone.",
          confirm: "Delete user",
          danger: true,
        });
        if (!ok) return;
        const r = await deleteUser(b.dataset.deluser!);
        if (!r.ok) alert(r.error);
        render();
      })
    );
    const err = app.querySelector<HTMLElement>("#nu-err")!;
    app.querySelector("#nu-add")?.addEventListener("click", async () => {
      err.textContent = "";
      const u = app.querySelector<HTMLInputElement>("#nu-user")!.value;
      const p = app.querySelector<HTMLInputElement>("#nu-pass")!.value;
      const role = app.querySelector<HTMLSelectElement>("#nu-role")!.value as Role;
      const r = await createUser(u, p, role);
      if (r.ok) render();
      else err.textContent = r.error || "Failed";
    });
    app.querySelector("#mig-run")?.addEventListener("click", async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      const out = app.querySelector<HTMLElement>("#mig-out")!;
      const tempPassword = app.querySelector<HTMLInputElement>("#mig-pass")!.value;
      btn.disabled = true;
      btn.textContent = "Importing…";
      try {
        const res = await migrateToCloud({
          tempPassword,
          users: legacy.map((u) => ({ username: u.username, role: u.role, data: legacyUserData(u.username) })),
        });
        out.innerHTML = `✓ Imported. New accounts: <b>${res.usersCreated.join(", ") || "none"}</b>${
          res.usersCreated.length ? ` (temporary password: <code>${esc(tempPassword)}</code>)` : ""
        } · already in cloud: ${res.usersExisting.join(", ") || "none"} · ${res.kvRows} data items · ${res.practiceRecords} practice markings${
          res.skipped.length ? `<br>Skipped: ${res.skipped.map(esc).join("; ")}` : ""
        }`;
        btn.textContent = "Import again";
      } catch (er) {
        out.textContent = (er as Error).message;
        btn.textContent = "Import to cloud";
      }
      btn.disabled = false;
    });

    // master practice data (mounted after the user-table wiring so its selectors don't clash)
    const pa = document.createElement("div");
    app.querySelector(".dash")!.appendChild(pa);
    mountPracticeAdmin(pa, me.username, users.map((u) => u.username));
  };
  await render();
}

function chartIcon() {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M7 14l3-4 3 2 4-6"/></svg>`;
}
function replayIcon() {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M10 8.4l6 3.6-6 3.6z" fill="currentColor" stroke="none"/></svg>`;
}
function adminIcon() {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="3.5"/><path d="M5 20a7 7 0 0114 0"/></svg>`;
}
function toolsIcon() {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a4 4 0 00-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 005.4-5.4l-2.5 2.5-2.5-.7-.7-2.5z"/></svg>`;
}
function testsIcon() {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 3.5h6v2H9z"/><path d="M8.5 11l1.5 1.5 3-3"/><path d="M8.5 16.5h7"/></svg>`;
}
function practiceIcon() {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/></svg>`;
}
function analysisIcon() {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4v16h16"/><rect x="7" y="11" width="3" height="6"/><rect x="12" y="7" width="3" height="10"/><rect x="17" y="13" width="3" height="4"/></svg>`;
}
